import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/node/index.js';
import { createCMMSView } from '../views/views.js';
import { getAuth } from 'firebase/auth';

const COLLECTION_NAME = 'parts';

// Helper function to get current user's company ID
async function getCurrentUserCompanyId(db) {
  const auth = getAuth(db.app);
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const userDoc = await getDocumentById(db, 'users', currentUser.uid);
  
  if (!userDoc || !userDoc.company_id) {
    throw new Error('User company not found');
  }

  return userDoc.company_id;
}

async function getParts(db) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'company_id', operator: '==', value: companyId }
    ]);
    
    const parts = docs.map((doc) => {
      return { id: doc.$id, ...doc };
    });
    return createCMMSView({ data: parts });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getPartsByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, { key: 'company_id', operator: '==', value: companyId });
    const parts = docs.map((doc) => {
      return { id: doc.$id, ...doc };
    });
    return createCMMSView({ data: parts });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getPartById(db, id) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    const part = await getDocumentById(db, COLLECTION_NAME, id);
    if (!part) return createCMMSView({ error: 'Part not found' });
    if (part.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Part not found' });
    }
    return createCMMSView({ data: { id: part.$id, ...part } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function createPart(db, partData) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    
    const partDoc = {
      ...partData,
      company_id: companyId,
      created_at: Date.now(),
      updated_at: Date.now(),
    };

    const docRef = await addDocToCollection(db, COLLECTION_NAME, partDoc);

    return createCMMSView({ data: { id: docRef.id, ...partDoc } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function updatePart(db, id, updateData) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    
    // First get the existing part to check company access
    const existingPart = await getDocumentById(db, COLLECTION_NAME, id);
    if (!existingPart) {
      return createCMMSView({ error: 'Part not found' });
    }
    if (existingPart.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Part not found' });
    }

    const updateDoc = {
      ...updateData,
      updated_at: Date.now(),
    };

    await updateDocById(db, COLLECTION_NAME, id, updateDoc);

    return createCMMSView({ data: { id, ...updateDoc } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getPartsByAsset(db, assetId, companyId = null) {
  try {
    // Auto-get companyId if not provided
    if (!companyId) {
      companyId = await getCurrentUserCompanyId(db);
    }

    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'asset_id', operator: '==', value: assetId },
      { key: 'company_id', operator: '==', value: companyId }
    ]);
    
    const parts = docs.map((doc) => {
      return { id: doc.$id, ...doc };
    });
    return createCMMSView({ data: parts });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function deletePart(db, id) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    
    // First get the existing part to check company access
    const existingPart = await getDocumentById(db, COLLECTION_NAME, id);
    if (!existingPart) {
      return createCMMSView({ error: 'Part not found' });
    }
    if (existingPart.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Part not found' });
    }
    
    await deleteDocByCollectionAndId(db, COLLECTION_NAME, id);

    return createCMMSView({ data: { id, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

export {
  getParts,
  getPartsByCompany,
  getPartsByAsset,
  getPartById,
  createPart,
  updatePart,
  deletePart,
};
